import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { syntaxHighlighting } from '@codemirror/language';

/**
 * Shared CodeMirror 6 theme for every editor surface in the app —
 * {@link TextEditorView} and the conflict {@link MergeView} (the merge view
 * reuses the SAME theme construction).
 *
 * Built from the app's CSS custom properties (the Tailwind token set in
 * globals.css) so light and dark both follow the app — no bundled editor
 * theme. This module rides the same lazy chunks as its consumers (both are
 * behind React.lazy), so the base bundle pays nothing.
 */

const tokenColor = (hue: string) => `color-mix(in oklab, ${hue} 60%, hsl(var(--foreground)))`;

export const editorTheme = EditorView.theme({
  '&': {
    height: '100%',
    backgroundColor: 'hsl(var(--background))',
    color: 'hsl(var(--foreground))',
    fontSize: '0.8125rem',
  },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': {
    overflow: 'auto',
    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
  },
  '.cm-content': { caretColor: 'hsl(var(--foreground))' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'hsl(var(--foreground))' },
  '.cm-gutters': {
    backgroundColor: 'hsl(var(--muted) / 0.3)',
    color: 'hsl(var(--muted-foreground))',
    borderRight: '1px solid hsl(var(--border))',
  },
  '.cm-selectionBackground, &.cm-focused .cm-selectionBackground': {
    backgroundColor: 'hsl(var(--accent))',
  },
  '.cm-selectionMatch': { backgroundColor: 'hsl(var(--accent) / 0.6)' },
  '.cm-searchMatch': {
    backgroundColor: 'hsl(var(--accent))',
    outline: '1px solid hsl(var(--border))',
  },
  '.cm-searchMatch.cm-searchMatch-selected': { backgroundColor: 'hsl(var(--accent) / 0.8)' },
  '.cm-panels': {
    backgroundColor: 'hsl(var(--background))',
    color: 'hsl(var(--foreground))',
  },
  '.cm-panels.cm-panels-top': { borderBottom: '1px solid hsl(var(--border))' },
  '.cm-panels.cm-panels-bottom': { borderTop: '1px solid hsl(var(--border))' },
  '.cm-tok-comment': { color: 'hsl(var(--muted-foreground))', fontStyle: 'italic' },
  '.cm-tok-keyword': { color: tokenColor('hsl(275 65% 50%)') },
  '.cm-tok-string': { color: tokenColor('hsl(120 45% 40%)') },
  '.cm-tok-constant': { color: tokenColor('hsl(30 80% 45%)') },
  '.cm-tok-type': { color: tokenColor('hsl(200 80% 45%)') },
  '.cm-tok-function': { color: tokenColor('hsl(220 80% 50%)') },
  '.cm-tok-property': { color: tokenColor('hsl(220 60% 50%)') },
  '.cm-tok-tag': { color: tokenColor('hsl(350 70% 45%)') },
  '.cm-tok-operator': { color: 'hsl(var(--muted-foreground))' },
  '.cm-tok-punctuation': { color: 'hsl(var(--muted-foreground))' },
  '.cm-tok-meta': { color: 'hsl(var(--muted-foreground))' },
  '.cm-tok-invalid': { color: 'hsl(var(--destructive))' },
  '.cm-tok-heading': { fontWeight: '600' },
  '.cm-tok-emphasis': { fontStyle: 'italic' },
  '.cm-tok-strong': { fontWeight: '600' },
  '.cm-tok-link': { textDecoration: 'underline' },
});

const TOKEN_CLASS: Record<string, string> = {
  comment: 'cm-tok-comment',
  keyword: 'cm-tok-keyword',
  string: 'cm-tok-string',
  regexp: 'cm-tok-string',
  escape: 'cm-tok-string',
  character: 'cm-tok-string',
  number: 'cm-tok-constant',
  bool: 'cm-tok-constant',
  atom: 'cm-tok-constant',
  null: 'cm-tok-constant',
  unit: 'cm-tok-constant',
  color: 'cm-tok-constant',
  typeName: 'cm-tok-type',
  className: 'cm-tok-type',
  namespace: 'cm-tok-type',
  'function(variableName)': 'cm-tok-function',
  'function(propertyName)': 'cm-tok-function',
  macroName: 'cm-tok-function',
  labelName: 'cm-tok-function',
  propertyName: 'cm-tok-property',
  attributeName: 'cm-tok-property',
  attributeValue: 'cm-tok-string',
  tagName: 'cm-tok-tag',
  operator: 'cm-tok-operator',
  punctuation: 'cm-tok-punctuation',
  meta: 'cm-tok-meta',
  invalid: 'cm-tok-invalid',
  heading: 'cm-tok-heading',
  emphasis: 'cm-tok-emphasis',
  strong: 'cm-tok-strong',
  link: 'cm-tok-link',
  url: 'cm-tok-link',
};

export const appSyntaxHighlighting = syntaxHighlighting({
  style(tags) {
    let cls: string | null = null;
    for (const tag of tags) {
      for (const t of tag.set) {
        const match = TOKEN_CLASS[String(t)];
        if (match) {
          cls = cls ? `${cls} ${match}` : match;
          break;
        }
      }
    }
    return cls;
  },
});

/**
 * Change decorations shared by every comparison surface — the editable
 * conflict {@link MergeView} and the read-only {@link VersionComparisonView}.
 * Built from the same app tokens as {@link editorTheme}, so both follow
 * light/dark with the rest of the app.
 */
export const changeHighlightTheme = EditorView.theme({
  '.cm-changedLine': { backgroundColor: 'color-mix(in oklab, hsl(200 80% 45%) 10%, transparent)' },
  '.cm-changedText': { background: 'color-mix(in oklab, hsl(200 80% 45%) 28%, transparent)' },
  '.cm-deletedChunk': { backgroundColor: 'color-mix(in oklab, hsl(350 70% 45%) 10%, transparent)' },
  '.cm-deletedText, del': {
    background: 'color-mix(in oklab, hsl(350 70% 45%) 28%, transparent)',
    textDecoration: 'none',
  },
  '.cm-collapsedLines': {
    color: 'hsl(var(--muted-foreground))',
    backgroundColor: 'hsl(var(--muted) / 0.3)',
  },
});

/** The extension set every comparison surface builds each document from. */
export function comparisonBaseExtensions() {
  return [
    lineNumbers(),
    history(),
    keymap.of([...defaultKeymap, ...historyKeymap]),
    editorTheme,
    appSyntaxHighlighting,
    changeHighlightTheme,
  ];
}
